import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { Reveal, Eyebrow, MaskedLine } from '../components/Reveal';
import api from '../lib/api';
import { INSIGHT_CATEGORIES, INSIGHT_TEASERS } from '../data/content';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

function readingTime(content) {
  if (!content) return 1;
  return Math.max(1, Math.round(content.split(/\s+/).length / 200));
}

export default function Insights() {
  const [articles, setArticles] = useState(null);
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');

  useEffect(() => {
    api.get('/insights').then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  const hasArticles = articles && articles.length > 0;

  const filtered = useMemo(() => {
    if (!hasArticles) return [];
    return articles.filter((a) => {
      const matchCat = category === 'All' || a.category === category;
      const matchQ = !query || a.title.toLowerCase().includes(query.toLowerCase()) || (a.summary || '').toLowerCase().includes(query.toLowerCase());
      return matchCat && matchQ;
    });
  }, [hasPublishedGuard(articles), articles, category, query]);

  function hasPublishedGuard(a) { return a && a.length > 0; }

  return (
    <>
      <SEO
        title="Insights | INSIGHTS STRATEGY — Perspectives That Shape Decisions"
        description="Thought leadership and research perspectives on AI, the digital economy, market intelligence, public policy, MSMEs, infrastructure, sustainability and consumer behaviour."
        path="/insights"
      />
      <section className="relative overflow-hidden bg-brand-deep" data-testid="insights-hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(23,107,58,0.16),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-44 lg:px-8 lg:pb-24">
          <Eyebrow dark>Insights &amp; Perspectives</Eyebrow>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="insights-headline">
            <MaskedLine delay={0.15}>Perspectives That</MaskedLine>
            <MaskedLine delay={0.3}>Shape <span className="text-brand-green">Decisions.</span></MaskedLine>
          </h1>
        </div>
      </section>

      {hasArticles ? (
        <section className="bg-brand-offwhite">
          <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full max-w-md">
                <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-body" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search insights…"
                  aria-label="Search insights"
                  data-testid="insights-search"
                  className="w-full rounded-full border border-brand-grey bg-white py-3 pl-11 pr-4 text-sm outline-none transition-colors focus:border-brand-green"
                />
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Filter insights by category">
                {['All', ...INSIGHT_CATEGORIES].map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    data-testid={`insight-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                      category === c ? 'border-brand-green bg-brand-green text-white' : 'border-brand-grey bg-white text-brand-black/70 hover:border-brand-green/50'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a, i) => (
                <Reveal key={a.insight_id} delay={0.05 * i}>
                  <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-brand-grey bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(11,13,11,0.1)]">
                    {a.cover_image && (
                      <div className="h-48 overflow-hidden">
                        <img src={a.cover_image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col p-6">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-deepgreen">{a.category}</span>
                      <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-brand-black">{a.title}</h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-body">{a.summary}</p>
                      <div className="mt-5 flex items-center justify-between text-xs text-brand-body">
                        <span>{[a.author, formatDate(a.published_at)].filter(Boolean).join(' · ')}</span>
                        <span>{readingTime(a.content)} min read</span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
              {filtered.length === 0 && <p className="col-span-full py-12 text-center text-sm text-brand-body" data-testid="insights-empty">No insights match your search.</p>}
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-brand-offwhite" data-testid="insights-coming-soon">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
            <Reveal>
              <div className="rounded-3xl border border-brand-green/25 bg-white p-8 sm:p-12">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-deep text-brand-green">
                    <Sparkles size={22} />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-brand-black sm:text-3xl">Our Insights hub is launching soon.</h2>
                    <p className="mt-3 max-w-2xl text-base leading-relaxed text-brand-body">
                      We are preparing research perspectives, market intelligence briefings and policy commentary. Every article we publish is reviewed and approved before release — the areas below preview the themes our knowledge centre will cover.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {INSIGHT_TEASERS.map((t, i) => (
                <Reveal key={t.category} delay={0.07 * i}>
                  <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-brand-grey bg-white" data-testid={`insight-teaser-${i}`}>
                    <div className="relative h-44 overflow-hidden">
                      <img src={t.image} alt={t.category} loading="lazy" className="h-full w-full object-cover opacity-85 transition-transform duration-700 group-hover:scale-105" />
                      <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-deepgreen backdrop-blur-sm">Forthcoming</span>
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-heading text-lg font-bold leading-snug text-brand-black">{t.category}</h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-body">{t.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-12 flex flex-wrap items-center gap-4">
                <p className="text-sm text-brand-body">Have a research question in the meantime?</p>
                <Link to="/contact" data-testid="insights-cta-contact" className="group inline-flex items-center gap-2 rounded-full bg-brand-green px-6 py-3 font-heading text-sm font-bold text-white transition-colors duration-300 hover:bg-brand-deepgreen">
                  Let's Talk <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}
    </>
  );
}
